import type { AnalyticsMetric, MetricDimension } from "./analytics-metrics.ts";
import { boundInstantMs, isDayBound } from "./buckets.ts";
import type { BucketInterval } from "./buckets.ts";

const DAY_MS = 86_400_000;

export const isoDay = (at: Date): string => at.toISOString().slice(0, 10);

export const shiftDays = (day: string, days: number): string =>
  isoDay(new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS));

export const dayIn = (instant: Date, timeZone: string | null): string => {
  if (timeZone === null) return isoDay(instant);
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone }).format(instant);
  } catch {
    return isoDay(instant);
  }
};

export const PRESETS = ["24h", "7d", "30d", "90d", "12m"] as const;

export type Preset = (typeof PRESETS)[number];

export const DEFAULT_PRESET: Preset = "7d";

const PRESET_DAYS: Readonly<Record<Preset, number>> = {
  "24h": 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
  "12m": 365,
};

export const presetSince = (today: string, preset: Preset): string =>
  shiftDays(today, 1 - PRESET_DAYS[preset]);

const spanDays = (since: string, until: string): number =>
  Math.round((boundInstantMs(until, "until") - boundInstantMs(since, "since")) / DAY_MS);

const COARSER_ABOVE: readonly (readonly [BucketInterval, number])[] = [
  ["hour", 2],
  ["day", 62],
  ["week", 186],
];

export const intervalFor = (since: string, until: string): BucketInterval => {
  const days = spanDays(since, until);
  const found = COARSER_ABOVE.find(([, limit]) => days <= limit);
  return found === undefined ? "month" : found[0];
};

export interface QueryScope {
  readonly guildId: string;
  readonly since: string;
  readonly until: string;
  readonly today: string;
  readonly interval: BucketInterval;
  readonly rolesInclude: readonly string[];
  readonly rolesExclude: readonly string[];
  readonly channelsInclude: readonly string[];
  readonly channelsExclude: readonly string[];
}

const shiftBound = (bound: string, days: number): string =>
  isDayBound(bound)
    ? shiftDays(bound, days)
    : new Date(Date.parse(bound) + days * DAY_MS).toISOString();

export const maturedWindow = (
  scope: Pick<QueryScope, "since" | "until" | "today">,
  days: number,
): { readonly since: string; readonly until: string } => {
  const lag = Math.floor(
    (boundInstantMs(scope.today, "until") - boundInstantMs(scope.until, "until")) / DAY_MS,
  );
  const shift = Math.max(0, days - lag);
  return { since: shiftBound(scope.since, -shift), until: shiftBound(scope.until, -shift) };
};

export const rolesQuery = (
  scope: Pick<QueryScope, "rolesInclude" | "rolesExclude">,
): { rolesInclude?: string; rolesExclude?: string } => ({
  ...(scope.rolesInclude.length > 0 ? { rolesInclude: scope.rolesInclude.join(",") } : {}),
  ...(scope.rolesExclude.length > 0 ? { rolesExclude: scope.rolesExclude.join(",") } : {}),
});

export const channelsQuery = (
  scope: Pick<QueryScope, "channelsInclude" | "channelsExclude">,
): { channelsInclude?: string; channelsExclude?: string } => ({
  ...(scope.channelsInclude.length > 0 ? { channelsInclude: scope.channelsInclude.join(",") } : {}),
  ...(scope.channelsExclude.length > 0 ? { channelsExclude: scope.channelsExclude.join(",") } : {}),
});

export type ScopeRead = "roles" | "channels" | "bucket";

export const scoped = (scope: QueryScope, ...reads: readonly ScopeRead[]) => ({
  guildId: scope.guildId,
  since: scope.since,
  until: scope.until,
  ...(reads.includes("roles") ? rolesQuery(scope) : {}),
  ...(reads.includes("channels") ? channelsQuery(scope) : {}),
  ...(reads.includes("bucket") ? { interval: scope.interval } : {}),
});

export interface Narrowing {
  readonly userId?: string;
  readonly channelId?: string;
  readonly limit?: number;
  readonly compare?: boolean;
}

export const askMetrics = <Metric extends AnalyticsMetric>(
  scope: QueryScope,
  metrics: readonly Metric[],
  groupBy: readonly MetricDimension[],
  reads: readonly ScopeRead[],
  narrowing: Narrowing = {},
) => ({
  ...scoped(scope, ...reads),
  metrics: [...metrics],
  groupBy: [...groupBy],
  ...(narrowing.userId === undefined ? {} : { userId: narrowing.userId }),
  ...(narrowing.channelId === undefined ? {} : { channelId: narrowing.channelId }),
  ...(narrowing.limit === undefined ? {} : { limit: narrowing.limit }),
  ...(narrowing.compare === true ? { compare: "previous" as const } : {}),
});

export const metricQuery = <Metric extends AnalyticsMetric>(
  scope: QueryScope,
  metric: Metric,
  groupBy: readonly MetricDimension[],
  ...reads: readonly ScopeRead[]
) => askMetrics(scope, [metric], groupBy, reads, { compare: true });

export const COHORT_WINDOW_DAYS = 7;

export const cohortQueryOver = (scope: QueryScope, days: number, compare = false) => ({
  ...scoped(scope, "roles", "bucket"),
  ...maturedWindow(scope, days),
  days,
  ...(compare ? { compare: "previous" as const } : {}),
});
